import { API_BASE, api } from '../lib/api';

export interface ExtractionListRow {
  id: number;
  extractionCode: string;
  extractionDate: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  apiarySiteId: number | null;
  productionSiteId: number;
  apiaryName: string | null;
  productionSiteName: string;
  totalKg: number | null;
}

export interface ExtractionOutput {
  id: number;
  containerId: number;
  productId: number;
  qtyKg: number;
  waterPercent: number | null;
  sortOrder: number;
  containerCode: string;
  kind: string;
  statusItemId: number | null;
  statusLabel: string | null;
  productName: string;
}

export interface ExtractionDocument {
  id: number;
  title: string | null;
  documentDate: string | null;
  notes: string | null;
  mediaId: number;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

export interface ExtractionDetails {
  id: number;
  extractionCode: string;
  extractionDate: string;
  status: 'DRAFT' | 'POSTED';
  notes: string | null;
  apiarySiteId: number | null;
  productionSiteId: number;
  nectarItemId: number | null;
  apiaryName: string | null;
  productionSiteName: string;
  nectarLabel: string | null;
  outputs: ExtractionOutput[];
  documents: ExtractionDocument[];
}

export interface ExtractionMeta {
  products: Array<{ id: number; name: string; kind: string }>;
  apiaries: Array<{ id: number; name: string }>;
  sites: Array<{ id: number; name: string }>;
  nectars: Array<{ id: number; label: string }>;
  statuses: Array<{ id: number; label: string }>;
  defaultProductionSiteId: number | null;
}

export interface ExtractionCurrentYearStats {
  year: number;
  extractedKg: number;
  outputCount: number;
}

export const extractionsApi = {
  list: (year: number) => api.get<{ extractions: ExtractionListRow[] }>(`/extractions?year=${year}`),
  get: (id: number) => api.get<ExtractionDetails>(`/extractions/${id}`),
  meta: () => api.get<ExtractionMeta>('/extractions/meta'),
  create: (payload: {
    extractionDate: string;
    apiarySiteId?: number | null;
    productionSiteId: number;
    nectarItemId?: number | null;
    notes?: string;
    outputs: Array<{
      productId: number;
      qtyKg: number;
      waterPercent?: number | null;
      statusItemId?: number | null;
    }>;
  }) => api.post<{ id: number; extractionCode: string }>('/extractions', payload),
  update: (id: number, payload: {
    extractionDate: string;
    apiarySiteId?: number | null;
    nectarItemId?: number | null;
    notes?: string;
    outputs: Array<{
      id?: number;
      productId: number;
      qtyKg: number;
      waterPercent?: number | null;
      statusItemId?: number | null;
    }>;
  }) => api.patch<{ ok: boolean }>(`/extractions/${id}`, payload),
  post: (id: number) => api.post<{ ok: boolean; txId: number }>(`/extractions/${id}/post`),
  updateOutputStatus: (containerId: number, statusItemId: number) =>
    api.patch<{ ok: boolean }>(`/extractions/outputs/${containerId}/status`, { statusItemId }),
  uploadDocument: (id: number, payload: {
    filename: string;
    mimeType: string;
    contentBase64: string;
    title?: string;
    documentDate?: string | null;
    notes?: string;
  }) => api.post<{ ok: boolean; documentId: number; mediaId: number }>(`/extractions/${id}/documents`, payload),
  deleteDocument: (documentId: number) => api.delete<{ ok: boolean }>(`/extractions/documents/${documentId}`),
  getDocumentUrl: (documentId: number) => `${API_BASE}/extractions/documents/${documentId}`,
  currentYearStats: () => api.get<ExtractionCurrentYearStats>('/extractions/stats/current-year'),
};
